import React from 'react';
import {
  Flex,
  Box,
  Table,
  Tbody,
  Text,
  Thead,
  Tr,
  useColorModeValue,
  IconButton,
  Badge,
} from '@chakra-ui/react';
import {
  createColumnHelper,
} from '@tanstack/react-table';
import { FaEye, FaTrash } from 'react-icons/fa';
import PageTable from 'components/layout/PageTable';
import AdminTableCell from 'components/layout/AdminTableCell';
import SortableTableHeader from 'components/layout/SortableTableHeader';
import { useSortableAdminTable } from 'hooks/useSortableAdminTable';
import { getSortableColumnOptions } from 'utils/tableColumnOptions';
import TablePagination from 'components/layout/TablePagination';

const columnHelper = createColumnHelper();

export default function CampaignsTable({
  columnsData,
  data,
  page,
  setPage,
  totalPages,
  pageSize,
  setPageSize,
  onView,
  onDelete,
  readOnly = false,
  sorting,
  onSortingChange,
}) {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const createColumns = (columnData) => {
    return columnData.map((col) => {
      if (col.accessor === 'active') {
        return columnHelper.accessor(col.accessor, {
          ...getSortableColumnOptions(col),
          header: () => (
            <Text
              justifyContent="space-between"
              align="center"
              fontSize={{ sm: '10px', lg: '12px' }}
              color="gray.400"
            >
              {col.Header}
            </Text>
          ),
          cell: (info) => (
            <Badge
              colorScheme={info.getValue() ? 'green' : 'red'}
              variant="solid"
            >
              {info.getValue() ? 'Activa' : 'Inactiva'}
            </Badge>
          ),
        });
      }

      if (col.accessor === 'chassisInfo') {
        return columnHelper.accessor(col.accessor, {
          ...getSortableColumnOptions(col),
          header: () => (
            <Text
              justifyContent="space-between"
              align="center"
              fontSize={{ sm: '10px', lg: '12px' }}
              color="gray.400"
            >
              {col.Header}
            </Text>
          ),
          cell: (info) => {
            const campaign = info.row.original;
            const total = campaign.chassisCount || 0;
            const reviewed = campaign.reviewedCount || 0;
            return (
              <Text fontSize="sm" fontWeight="normal">
                {reviewed}/{total} revisados
              </Text>
            );
          },
        });
      }

      if (col.accessor === 'actions') {
        return columnHelper.accessor(col.accessor, {
          ...getSortableColumnOptions(col),
          header: () => (
            <Text
              justifyContent="space-between"
              align="center"
              fontSize={{ sm: '10px', lg: '12px' }}
              color="gray.400"
            >
              {col.Header}
            </Text>
          ),
          cell: (info) => (
            <Flex>
              <IconButton
                icon={<FaEye />}
                colorScheme="gray"
                variant="ghost"
                size="sm"
                mr={2}
                onClick={() => onView(info.row.original)}
              />
              {!readOnly && (
                <IconButton
                  icon={<FaTrash />}
                  colorScheme="red"
                  variant="ghost"
                  size="sm"
                  onClick={() => onDelete(info.row.original)}
                />
              )}
            </Flex>
          ),
        });
      }

      if (col.accessor === 'description') {
        return columnHelper.accessor(col.accessor, {
          ...getSortableColumnOptions(col),
          header: () => (
            <Text
              justifyContent="space-between"
              align="center"
              fontSize={{ sm: '10px', lg: '12px' }}
              color="gray.400"
            >
              {col.Header}
            </Text>
          ),
          cell: (info) => (
            <Text
              fontSize="sm"
              fontWeight="normal"
              noOfLines={2}
              maxW="300px"
            >
              {info.getValue()}
            </Text>
          ),
        });
      }

      return columnHelper.accessor(col.accessor, {
        ...getSortableColumnOptions(col),
        header: () => (
          <Text
            justifyContent="space-between"
            align="center"
            fontSize={{ sm: '10px', lg: '12px' }}
            color="gray.400"
          >
            {col.Header}
          </Text>
        ),
        cell: (info) => (
          <Text fontSize="sm" fontWeight="normal">
            {info.getValue()}
          </Text>
        ),
      });
    });
  };
  const columns = React.useMemo(
    () => createColumns(columnsData),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnsData, onView, onDelete, readOnly],
  );

  const table = useSortableAdminTable({
    data,
    columns,
    sorting,
    onSortingChange,
  });

  return (
    <PageTable
      flexDirection="column"
      w="100%"
      px="0px"
     >
      <Flex px="25px" mb="8px" justifyContent="space-between" align="center">
        <Text
          color="secondaryGray.900"
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Campañas de Vehículos
        </Text>
      </Flex>
      <Box>
        <PageTable.Scroll>
          <Table variant="simple" color="gray.700" mb="24px" mt="12px">
                    <Thead>
                      {table.getHeaderGroups().map((headerGroup) => (
                        <Tr key={headerGroup.id}>
                          {headerGroup.headers.map((header) => (
                            <SortableTableHeader
                              key={header.id}
                              header={header}
                              borderColor={borderColor}
                            />
                          ))}
                        </Tr>
                      ))}
                    </Thead>
                    <Tbody>
                      {table.getRowModel().rows.map((row) => (
                        <Tr key={row.id}>
                          {row.getVisibleCells().map((cell) => (
                            <AdminTableCell
                              key={cell.id}
                              cell={cell}
                              fontSize={{ sm: '14px' }}
                            />
                          ))}
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
        </PageTable.Scroll>
      </Box>

      <TablePagination
        page={page}
        setPage={setPage}
        totalPages={totalPages}
        pageSize={pageSize}
        setPageSize={setPageSize}
      />
    </PageTable>
  );
}
